import type { ChatSession } from "@stockline/shared/types/ai/chat-session";
import { CHAT_NOTIFICATION_TYPE } from "@stockline/shared/types/ai/chat-session";
import {
  useActiveChatSession,
  useChatMessages,
  useChatMutations,
  useChatSessionSummaries,
} from "@stockline/web";
import { Loader2, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { resolveAiText } from "@/lib/ai-text";
import {
  differenceInCalendarDays,
  differenceInMinutes,
  format,
  isSameDay,
} from "date-fns";
import { Button } from "@stockline/ui/components/button";
import { ScrollArea } from "@stockline/ui/components/scroll-area";
import { cn } from "@/lib/utils";
import { dateLocaleFor } from "../triggers/TriggerCard";
import {
  useChatSessions,
  useChatSession,
  type ChatMode,
} from "./useChatSession";
import { ChatSessionActions } from "./ChatSessionActions";

interface ChatSessionListProps {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  mode: ChatMode;
  /**
   * Compact rendering for the floating panel: drops the per-row icon and
   * separates conversations with a divider instead of spaced cards. The full
   * `/ai` page keeps the default (iconed) layout.
   */
  compact?: boolean;
  /**
   * Show the Unread/History split, the read-state filter, and the unread dots.
   * "Unread" is now the chat session's own `unread` flag (a completed assistant
   * turn / proactive notification sets it server-side). Off (default) the list
   * behaves like a plain chat list — keeping the compact side-panel unchanged.
   */
  showNotifications?: boolean;
  /**
   * Drop Business-Intelligence briefs (`notificationType === "bi"`) from the
   * rendered list. Opt-in (default `false`) because this list is SHARED: the
   * general `/ai` inbox and the floating assistant set it `true` (BI briefs are
   * steering-only), while the steering BiChat dock leaves it off so its history
   * still shows those briefs.
   */
  excludeBi?: boolean;
}

type SessionFilter = "all" | "unread" | "read";

interface AnnotatedSession {
  session: ChatSession;
  /**
   * Whether the row renders as unread. NOT the raw `session.unread`: the
   * active+focused session is forced read at display time (the flicker fix), so
   * a reply landing on the chat you're viewing never flashes a dot or jumps to
   * the Unread section before the clear-unread push lands.
   */
  displayUnread: boolean;
}

export function ChatSessionList({
  selectedId,
  onSelect,
  mode,
  compact = false,
  showNotifications = false,
  excludeBi = false,
}: ChatSessionListProps) {
  const { t, i18n } = useTranslation();
  // One unified `chat_sessions` pool — there is no BI-vs-assistant surface tag.
  // The same list is shown on every surface; what makes a turn "BI" is the live
  // view context at send time, not the conversation it lives in.
  const allSessions = useChatSessions();
  const { create, deleteSession, flushSessions } = useChatSession(null, mode);
  // Tracks the in-flight create+flush so the New button is disabled and
  // re-entry is guarded — concurrent clicks would otherwise spawn duplicate
  // sessions and race the selection. (`useChatSession.isCreating` is a
  // hardcoded false, so the create flow owns this state locally.)
  const [isCreating, setIsCreating] = useState(false);

  const { markSessionRead, markSessionUnread } = useChatMutations();
  // The active+focused session is treated as read at display time (flicker fix)
  // — a reply landing on the chat in view must not flash a dot / jump to Unread.
  const { activeSessionId, isFocused } = useActiveChatSession();
  // Reactive last-message preview + per-session run state (one liveQuery over
  // the messages + executions tables) — drives line 2 and the working spinner.
  const sessionSummaries = useChatSessionSummaries();
  const [filter, setFilter] = useState<SessionFilter>("all");

  // Sticky Unread placement: once a session is seen unread this scope, it KEEPS
  // its spot in the Unread section even after being read — only the dot animates
  // away. Kept in state (not a render-time ref write) so updates are deterministic
  // and re-render correctly under StrictMode / React Compiler. Reset when the
  // scope changes (mode / showNotifications) so it never accumulates forever; a
  // read sticky session then settles into History on the next scope reset.
  const [stickyUnread, setStickyUnread] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // Scope reset: clear the sticky set whenever the list's scope changes, so a
  // long-lived merged Inbox tab doesn't pin read sessions under "Unread" forever.
  useEffect(() => {
    setStickyUnread(new Set());
  }, [mode, showNotifications]);

  const isStatsMode = mode === "statistics";

  // Sort by LAST MESSAGE time (not updatedAt) so marking a notification read
  // — a session write that bumps updatedAt — doesn't shove it to "now"/top.
  // Empty sessions fall back to createdAt, keeping a fresh empty session (the
  // reuse candidate) genuinely newest.
  const sessions = useMemo(() => {
    if (!allSessions) return allSessions;
    // BI briefs are steering-only when the caller opts out of them — keep them
    // off the general `/ai` inbox and the floating assistant list.
    const scoped = excludeBi
      ? allSessions.filter(
          (s) => s.notificationType !== CHAT_NOTIFICATION_TYPE.BI,
        )
      : allSessions;
    const activityAt = (s: ChatSession) =>
      new Date(
        sessionSummaries.get(s.id)?.lastMessageAt ?? s.createdAt,
      ).getTime();
    return [...scoped].sort((a, b) => activityAt(b) - activityAt(a));
  }, [allSessions, sessionSummaries, excludeBi]);

  // Reuse the most recent session if it's still empty (untitled + no
  // messages) — avoids spawning duplicates on repeated "New" clicks.
  // Only the latest is checked; older untitled sessions are left as-is.
  const mostRecentSession = sessions?.[0] ?? null;
  const mostRecentMessages = useChatMessages(
    mostRecentSession?.title === null ? mostRecentSession.id : null,
  );
  // `useChatMessages` (Dexie useLiveQuery) can transiently return undefined
  // on first render — strict `=== 0` keeps that window from being treated as
  // "empty" and falls through to creating a fresh session.
  const existingEmptySessionId =
    mostRecentSession?.title === null && mostRecentMessages?.length === 0
      ? mostRecentSession.id
      : null;

  const handleNewChat = async () => {
    // Guard re-entry: a second click while the first create+flush is still in
    // flight would create a duplicate session and race selection.
    if (isCreating) return;
    if (existingEmptySessionId) {
      onSelect(existingEmptySessionId);
      return;
    }
    setIsCreating(true);
    try {
      const session = await create();
      // Flush BEFORE selecting so the freshly-created session is persisted
      // server-side before it can become active and receive a first message.
      // `create` only fire-and-forgets its push to the offline-first
      // `chat-sessions` resource; selecting immediately would let the user send
      // a message that races ahead of its parent session and orphans (FK 500).
      // This mirrors the flush-before-send invariant `ChatWindow.ensureSession`
      // enforces on its own lazy-create path.
      await flushSessions();
      onSelect(session.id);
    } catch (err) {
      // A session that never reached the server must NOT become the active one,
      // or its first message would orphan. Surface the failure and leave the
      // current selection untouched so the user can retry.
      console.error("[chat-session-list] failed to start conversation", err);
      toast.error(t("ai.sendFailed"));
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelect = (session: ChatSession) => {
    if (session.unread) void markSessionRead(session.id);
    onSelect(session.id);
  };

  const handleDelete = (id: string) => {
    if (selectedId === id) {
      onSelect(null);
    }
    deleteSession(id);
  };

  const handleMarkUnread = (id: string) => {
    if (selectedId === id) {
      onSelect(null);
    }
    void markSessionUnread(id);
  };

  // Compact, right-aligned timestamp on every row. The list is sorted
  // most-recent-first to match. Buckets: `now` (<1m), `{m}m` (<1h), `{h}h`
  // (today/<24h), localized short weekday (<7d), else a short date (`Mar 3`).
  const formatCompactTime = (dateStr: string) => {
    const date = new Date(dateStr);
    // Defensive: a row without a usable timestamp must not crash the list
    // (date-fns `format` throws on an invalid date).
    if (Number.isNaN(date.getTime())) return "";
    const now = new Date();
    const locale = dateLocaleFor(i18n.language);
    const mins = differenceInMinutes(now, date);
    if (mins < 1) return t("common.aiManagement.sessionList.timeNow");
    if (mins < 60)
      return t("common.aiManagement.sessionList.timeMinutes", { m: mins });
    if (isSameDay(date, now))
      return t("common.aiManagement.sessionList.timeHours", {
        h: Math.floor(mins / 60),
      });
    if (differenceInCalendarDays(now, date) < 7)
      return format(date, "EEE", { locale });
    return format(date, "MMM d", { locale });
  };

  const emptyLabel = isStatsMode
    ? t("common.sidePanelChat.noQueriesYet")
    : t("common.sidePanelChat.noConversationsYet");
  const untitledLabel = isStatsMode
    ? t("common.sidePanelChat.newQuery")
    : t("common.sidePanelChat.newConversation");

  // Annotate each session with its display read-state. PURE: no ref/state writes
  // here (StrictMode double-invokes this factory, and a render-time side effect
  // would break React Compiler). When notifications are off this is a flat list
  // with displayUnread always false — identical to the plain chat list.
  const annotated = useMemo<AnnotatedSession[]>(
    () =>
      (sessions ?? []).map((session) => {
        // Flicker fix: the session the user is actively looking at (focused tab)
        // renders read no matter what `session.unread` says — so a reply landing
        // on the open chat never flashes a dot or jumps to Unread before the
        // clear-unread push lands.
        const isActiveFocused = session.id === activeSessionId && isFocused;
        const displayUnread =
          showNotifications && session.unread && !isActiveFocused;
        return { session, displayUnread };
      }),
    [sessions, showNotifications, activeSessionId, isFocused],
  );

  // Sticky bookkeeping lives OUTSIDE render: add any currently-unread session to
  // the sticky set so it holds its Unread-section slot even after being read
  // (only the dot animates away). Additive within a scope; cleared on scope reset
  // above. Functional update + bail-out keeps this from looping.
  useEffect(() => {
    const nowUnread = annotated
      .filter((a) => a.displayUnread)
      .map((a) => a.session.id);
    if (nowUnread.length === 0) return;
    setStickyUnread((prev) => {
      const next = new Set(prev);
      let added = false;
      for (const id of nowUnread) {
        if (!next.has(id)) {
          next.add(id);
          added = true;
        }
      }
      return added ? next : prev;
    });
  }, [annotated]);

  // Filter + section the list.
  // Semantics (documented rule, avoids a read row showing in BOTH places):
  //  - Unread SECTION = sticky membership (was unread this scope), regardless of
  //    live read-state — so reading a session doesn't yank it out mid-view, it
  //    just loses its dot. A sticky-but-read row belongs here visually.
  //  - "Read" FILTER = genuinely-read, non-sticky rows only. A sticky row (even
  //    once read) is conceptually still an Unread-section item, so it is excluded
  //    from the Read filter — it never appears under both.
  const { unread, history } = useMemo(() => {
    const filtered = annotated.filter((a) => {
      if (filter === "unread") return a.displayUnread;
      if (filter === "read")
        return !a.displayUnread && !stickyUnread.has(a.session.id);
      return true;
    });

    return {
      unread: filtered.filter((a) => stickyUnread.has(a.session.id)),
      history: filtered.filter((a) => !stickyUnread.has(a.session.id)),
    };
  }, [annotated, filter, stickyUnread]);

  const totalShown = unread.length + history.length;

  // Last-message preview for line 2 — the session's latest message text. Empty
  // conversations fall back to a localized "No messages yet".
  const previewFor = (annotated: AnnotatedSession): string => {
    const summary = sessionSummaries.get(annotated.session.id);
    // A notification session always previews its original notify_user body,
    // never the latest reply — so a brief keeps reading as its notification
    // even after the user has chatted in it.
    const text = summary?.notifyMessage ?? summary?.lastMessage;
    const trimmed = text?.replace(/\s+/g, " ").trim();
    return trimmed || t("common.aiManagement.sessionList.noMessagesYet");
  };

  const renderRow = (annotated: AnnotatedSession, inUnreadSection: boolean) => {
    const { session, displayUnread } = annotated;
    // Working state, derived from the latest execution per session. Takes
    // precedence over the unread dot — three distinct left-slot states:
    // working (spinner) > unread (violet dot) > history (nothing).
    const summary = sessionSummaries.get(session.id);
    const isWorking = !!summary?.isRunning || !!summary?.isWaitingInput;
    return (
      <div
        key={session.id}
        data-testid={`chat-session-${session.id}`}
        onClick={() => handleSelect(session)}
        className={cn(
          "relative flex items-center gap-2 cursor-pointer group",
          "hover:bg-accent transition-colors",
          compact ? "px-3 py-2.5" : "p-2 rounded-md",
          selectedId === session.id && "bg-accent",
        )}
      >
        {/* Left slot, in precedence order:
            1. Working → a small spinner (the AI is running / awaiting input).
            2. Unread-section row → a violet dot that collapses-left on read.
            3. Otherwise no slot, so History/regular titles sit flush left. */}
        {isWorking ? (
          <Loader2
            className="h-3 w-3 shrink-0 animate-spin text-violet-500"
            aria-hidden
            data-testid={`chat-session-working-${session.id}`}
          />
        ) : (
          inUnreadSection && (
            <span
              className={cn(
                "shrink-0 rounded-full bg-violet-500 transition-all duration-300 ease-out",
                displayUnread
                  ? "h-2 w-2 opacity-100"
                  : "h-2 w-0 -mr-2 opacity-0",
              )}
              aria-hidden
              data-testid={
                displayUnread ? `chat-session-unread-${session.id}` : undefined
              }
            />
          )
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                "flex-1 min-w-0 truncate text-sm",
                displayUnread && "font-semibold",
              )}
            >
              {session.title ? resolveAiText(session.title, t) : untitledLabel}
            </span>
            {/* Suppress the time while working so the spinner reads as the row's
                live state, not a stale timestamp. */}
            {!isWorking && (
              <span className="mr-6 shrink-0 text-xs text-muted-foreground">
                {formatCompactTime(summary?.lastMessageAt ?? session.createdAt)}
              </span>
            )}
          </div>
          {/* Right gutter so the preview cuts off cleanly before the row edge
              (and roughly under the title, not running past the time). */}
          <div className="truncate pr-8 text-xs text-muted-foreground">
            {previewFor(annotated)}
          </div>
        </div>
        <ChatSessionActions
          sessionId={session.id}
          unread={displayUnread}
          notificationMode={showNotifications}
          onMarkRead={() => void markSessionRead(session.id)}
          onMarkUnread={() => handleMarkUnread(session.id)}
          onClear={() => handleDelete(session.id)}
        />
      </div>
    );
  };

  const sectionHeader = (label: string) => (
    <h3 className="px-1 pt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </h3>
  );

  return (
    <div className="flex flex-col h-full" data-testid="chat-session-list">
      {/* New Chat Button */}
      <div className="p-3 border-b">
        <Button
          onClick={handleNewChat}
          disabled={isCreating}
          className="w-full"
          variant="outline"
          data-testid="new-chat-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          {isStatsMode
            ? t("common.sidePanelChat.newQueryButton")
            : t("common.sidePanelChat.newChatButton")}
        </Button>
      </div>

      {/* Read-state filter — always shown when the list participates in unread
          state, so the All/Unread/Read pills are available even with nothing
          currently unread. */}
      {showNotifications && (
        <div className="flex gap-1 border-b p-2" data-testid="session-filter">
          {(["all", "unread", "read"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              data-testid={`session-filter-${value}`}
              aria-pressed={filter === value}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                filter === value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-accent",
              )}
            >
              {value === "all" &&
                t("common.aiManagement.sessionList.filterAll")}
              {value === "unread" &&
                t("common.aiManagement.sessionList.filterUnread")}
              {value === "read" &&
                t("common.aiManagement.sessionList.filterRead")}
            </button>
          ))}
        </div>
      )}

      {/* Session List. `[&>div>div]:!block` forces the Radix viewport's inner
          wrapper off its default `display:table` (min-content width) so rows
          take the panel width and `truncate` actually works (same fix the AI
          execution list uses). */}
      <ScrollArea className="flex-1 [&>div>div]:!block">
        <div
          className={cn(
            "min-w-0",
            compact ? "divide-y divide-border" : "p-2 space-y-1",
          )}
        >
          {totalShown === 0 ? (
            <div className="text-center text-muted-foreground py-4 text-sm">
              {emptyLabel}
            </div>
          ) : (
            <>
              {unread.length > 0 && (
                <div className={cn(!compact && "space-y-1")}>
                  {sectionHeader(
                    t("common.aiManagement.sessionList.sectionUnread"),
                  )}
                  {unread.map((a) => renderRow(a, true))}
                </div>
              )}
              {history.length > 0 && (
                <div className={cn(!compact && "space-y-1")}>
                  {/* Only label the History section when there's an Unread
                      section above it to distinguish — otherwise the plain
                      list stays header-free, exactly as before. */}
                  {unread.length > 0 &&
                    sectionHeader(
                      t("common.aiManagement.sessionList.sectionHistory"),
                    )}
                  {history.map((a) => renderRow(a, false))}
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
